import { Typography, LazyImage } from '@frontend/design_system'
import { NavigationItemProps } from './types'
import { joinClasses } from 'utils/joinClasses'
import warning from 'assets/icons/red600/red600_warning_20x20.webp'
import checkIcon from 'assets/img/checkIcon.webp'
import styles from './styles.module.scss'
import { isMigratedUserSelector, userDataSelector } from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { MENU_ITEM_STATUS_MAP } from 'layouts/MainLayout/Menu/constants'
import { ACCOUNT_STATUSES, REGISTRATION_STATUSES } from 'api/auth/constants'
import { useMemo } from 'react'
import { MigrationSectionsStatuses, RegistrationStatuses } from 'api/auth/types'

export const NavigationItem = ({
  label,
  onClick,
  isActive,
  isDisabled,
}: NavigationItemProps) => {
  const { sectionsStatuses, migrationSectionsStatuses, accountStatus } =
    useAppSelector(userDataSelector)
  const isMigratedUser = useAppSelector(isMigratedUserSelector)
  const isApprovedAccount = accountStatus === ACCOUNT_STATUSES.APPROVED
  const getColor = () => {
    if (isDisabled) {
      return 'blueSecondary'
    } else if (isActive) {
      return 'blackMaster'
    }

    return 'blue700'
  }

  const shouldShowWarningIcon = useMemo(() => {
    if (typeof label === 'string' && isMigratedUser) {
      const sectionKey = MENU_ITEM_STATUS_MAP[
        label
      ] as keyof MigrationSectionsStatuses
      return (
        migrationSectionsStatuses?.[sectionKey] === REGISTRATION_STATUSES.REVIEW
      )
    }
    if (typeof label === 'string') {
      return (
        sectionsStatuses?.[
          MENU_ITEM_STATUS_MAP[label] as keyof RegistrationStatuses
        ] === REGISTRATION_STATUSES.REVIEW
      )
    }
  }, [sectionsStatuses, label, migrationSectionsStatuses])

  const shouldShowApprovedIcon = useMemo(() => {
    if (isApprovedAccount) {
      return false
    }

    if (typeof label === 'string' && isMigratedUser) {
      return (
        migrationSectionsStatuses?.[
          MENU_ITEM_STATUS_MAP[label] as keyof MigrationSectionsStatuses
        ] === REGISTRATION_STATUSES.APPROVED
      )
    }
    if (typeof label === 'string') {
      return (
        sectionsStatuses?.[
          MENU_ITEM_STATUS_MAP[label] as keyof RegistrationStatuses
        ] === REGISTRATION_STATUSES.APPROVED
      )
    }
    return false
  }, [sectionsStatuses, label, migrationSectionsStatuses])

  const renderIcon = () => {
    if (shouldShowWarningIcon) {
      return (
        <LazyImage
          src={warning}
          minWidth={20}
          height={20}
          dataTestId="warning-icon"
        />
      )
    }
    if (shouldShowApprovedIcon) {
      return (
        <LazyImage
          src={checkIcon}
          minWidth={20}
          height={20}
          dataTestId="approved-icon"
        />
      )
    }
    return null
  }

  return (
    <Typography
      Tag="div"
      name="body2WBold"
      onClick={isDisabled ? undefined : onClick}
      dataTestId="navigation-item"
      color={getColor()}
      className={joinClasses(
        styles.item,
        [styles.active, isActive],
        [styles.disabled, isDisabled]
      )}
    >
      {label}
      {renderIcon()}
    </Typography>
  )
}
